/**
 * @file useConsultationServices.js
 *
 * useConsultationServices
 *
 * Manages the list of services the logged-in professional offers.
 * Used in MyProfile.jsx to let professionals add/remove their services.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { consultationServiceApi } from "../services/consultationService";

/**
 * Hook for managing the logged-in professional's consultation services (CRUD).
 *
 * @returns {{services: Array, isLoading: boolean, addService: Function, updateService: Function, removeService: Function, refresh: Function}}
 */
export function useConsultationServices() {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchServices = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await consultationServiceApi.getAll();
      setServices(res.data || []);
    } catch {
      // Error handled by interceptor
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const addService = async (data) => {
    const res = await consultationServiceApi.create(data);
    setServices((prev) => [...prev, res.data]);
    toast.success("Servicio agregado");
    return res.data;
  };

  const updateService = async (id, data) => {
    const res = await consultationServiceApi.update(id, data);
    setServices((prev) => prev.map((s) => s.id === id ? res.data : s));
    toast.success("Servicio actualizado");
    return res.data;
  };

  const removeService = async (id) => {
    await consultationServiceApi.remove(id);
    setServices((prev) => prev.filter((s) => s.id !== id));
    toast.success("Servicio eliminado");
  };

  return { services, isLoading, addService, updateService, removeService, refresh: fetchServices };
}
